import { PaginationResult } from '@shared/pagination';
import { useQuery } from '@tanstack/react-query';
import { Box, CheckBox, CheckBoxGroup, FormField, TextInput } from 'grommet';
import { toString } from 'lodash';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Setting, SettingsList } from '@//components/setting';
import { Busy } from '@/components/busy';
import { ClassificationFilter } from '@/components/classification-filter';
import { LazyLoadMultiSelectList } from '@/components/form-controls';
import { Button } from '@/components-new/button';
import { Client } from '@/features/clients';
import { useUpdateSubscription } from '@/features/clients/api/use-update-subscription';
import { Subscription } from '@/features/clients/models/subscription';
import { SubscriptionFormInputs } from '@/features/clients/models/subscription-form-inputs';
import { useLookupsService } from '@/hooks/use-lookups-service';
import { Lookup } from '@/types/lookup';
import { useFeatures } from '@shared/feature-flags';
import { useAuth } from 'react-oidc-context';
import { hasRole, Role } from '@shared/auth';

export const SubscriptionTabContent = ({ client, subscription }: SubscriptionTabContentProps) => {
  const { id } = useParams();
  const { mutate, isPending } = useUpdateSubscription();
  const [isTrackingAllDrugs, setIsTrackingAllDrugs] = useState<boolean>(client.isTrackingAllDrugs);

  const { getFeature } = useFeatures();
  const isTrackedDrugMbRemovalOn = getFeature("trackedDrugMbRemoval");
  const {
    control,
    getValues,
    formState: { errors, isValid },
    handleSubmit
  } = useForm<SubscriptionFormInputs>({
    mode: 'all'
  });

  const { user } = useAuth();
  // @ts-expect-error TS(2345): Argument of type 'User | null | undefined' is not ... Remove this comment to see the full error message
  const canTrackAllDrugsForClient = hasRole(user, Role.Administrator);

  console.log(subscription);
  const { getClassifications, getProductLookups } = useLookupsService();

  const { data: filteredClassifications } = useQuery({
    queryKey: ['classifications'],
    queryFn: async () => {
      const data = await getClassifications();
      return data;
    }
  });

  const onSave = async (value: SubscriptionFormInputs) => {
    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
    mutate({ id: parseInt(id), form: value });
  };

  return (
    <Box>
      <SettingsList>
        <Setting title="Subscription Status">
          <Controller
            control={control}
            name="isActive"
            defaultValue={subscription.isActive}
            render={({ field: { value, onChange, onBlur } }) => (
              <CheckBox
                checked={value}
                label="Active"
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
        </Setting>

        <Setting title="Plan Limits">
          <Controller
            control={control}
            name="numberOfUsers"
            defaultValue={toString(subscription.numberOfUsers)}
            rules={{
              maxLength: 4,
              required: false,
              min: 0
            }}
            render={({ field: { ref, value, onChange, onBlur } }) => (
              <FormField
                name="numberOfUsers"
                htmlFor="numberOfUsers"
                label="Number of Users"
                error={errors.numberOfUsers?.message}
              >
                <TextInput
                  type="number"
                  ref={ref}
                  // @ts-expect-error TS(2322): Type 'string | null | undefined' is not assignable... Remove this comment to see the full error message
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  id="numberOfUsers"
                  name="numberOfUsers"
                  maxLength={4}
                  min={0}
                  max={9999}
                />
              </FormField>
            )}
          />
        </Setting>

        <Setting title="Plan Features (Capsule)" description="Which features are available to this client in capsule.">
          <Controller
            control={control}
            name="premiumFeatures"
            defaultValue={subscription.premiumFeatures
              .filter(feature => feature.isEnabled)
              .map(feature => feature.key)}
            render={({ field: { value, onChange } }) => (
              <CheckBoxGroup
                options={subscription.premiumFeatures.filter(feature => !feature.key.endsWith('_mobile') && feature.key !== 'ai_insights')}
                labelKey="name"
                valueKey="key"
                value={value}
                onChange={(event) => {
                  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                  onChange((event.value as unknown) as string[]);
                }}
              />
            )}
          />
        </Setting>

        <Setting title="Plan Features (Mobile)" description="Which features are available to this client in mobile.">
          <Controller
            control={control}
            name="premiumFeatures"
            defaultValue={subscription.premiumFeatures
              .filter(feature => feature.isEnabled && feature.key.endsWith('_mobile'))
              .map(feature => feature.key)}
            render={({ field: { value, onChange } }) => (
              <CheckBoxGroup
                options={subscription.premiumFeatures.filter(feature => feature.key.endsWith('_mobile'))}
                labelKey="name"
                valueKey="key"
                value={value}
                onChange={(event) => {
                  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                  onChange((event.value as unknown) as string[]);
                }}
              />
            )}
          />
        </Setting>

        <Setting title="Tracking">
          <Controller
            control={control}
            name="isTrackingAllDrugs"
            defaultValue={client.isTrackingAllDrugs}
            render={({ field: { value, onChange, onBlur } }) => (
              <>
              <CheckBox
                checked={value}
                disabled={!canTrackAllDrugsForClient}
                label="All Tracked Drugs"
                onChange={(e) => {
                  setIsTrackingAllDrugs(e.target.checked);
                  onChange(e.target.checked);
                }}
                onBlur={onBlur}
              />
                <p className={'text-gray-500'}>This will sync this clients tracked drugs to all drugs being tracked
                  by all clients. <span className={'font-bold'}>Admin use only.</span></p>
              </>
            )}
          />

          {(isTrackingAllDrugs || client.isTrackingAllDrugs) &&
            <>
              <h1 className={'font-bold text-sm'}>Tracked Drugs</h1>
              {(!isTrackingAllDrugs && client.isTrackingAllDrugs) &&
                <p className={'text-red-500'}>You must click Save before updating Tracked Drugs.</p>
              }
              {isTrackingAllDrugs &&
                <p className={'text-gray-500'}>Individual drug selection is disabled due to "All Tracked Drugs" being selected.</p>
              }
            </>
          }
          {(!isTrackingAllDrugs && !client.isTrackingAllDrugs) &&
            <>
              <Controller
                control={control}
                name="trackedDrugs"
                defaultValue={client.trackedClinicalsProducts}
                render={({ field, fieldState: { error } }) => (
                  <LazyLoadMultiSelectList
                    label="Tracked Drugs"
                    help="Track PDL updates for each of these drugs"
                    showSelectedInline
                    lazyLoadRequest={async (searchTerm, page, rpp) => {
                      const result = await getProductLookups({ query: searchTerm, page, rpp });
                      return result as PaginationResult<Lookup>;
                    }}
                    {...field}
                    error={error?.message}
                  />
                )}
              />
              {isTrackedDrugMbRemovalOn &&
                <p>
                  * Drugs removed from the tracked list will also be removed from any market baskets belonging to this client.
                </p>
              }
            </>
          }

          <Controller
            control={control}
            name="trackedClassifications"
            defaultValue={client.trackedClassifications}
            render={({ field: { value, onChange } }) => (
              <ClassificationFilter
                label="Tracked Classes"
                help="Track PDL updates for every drug in these classes"
                showSelectedInline={true}
                classifications={filteredClassifications ?? []}
                selected={value}
                onSelectionChange={onChange}/>
            )}
          />
        </Setting>
      </SettingsList>

      <Box direction="row" justify="end" gap="small">
        <Button
          onClick={handleSubmit(onSave)}
          disabled={isPending || !isValid}
        >
          <Busy busy={false} content="Save"/>
        </Button>
      </Box>
    </Box>
  );
};

type SubscriptionTabContentProps = {
  client: Client;
  subscription: Subscription;
};
